import { useCallback, useRef } from "react";
import type { KeyboardEvent } from "react";

/**
 * WAI-ARIA toolbar keyboard pattern.
 *
 * Use for a row of independent action / toggle buttons (e.g. Auto-rotate,
 * Show labels, Excite). Unlike a radiogroup, activation is independent -
 * arrow keys only move focus, they don't change state.
 *
 * Usage:
 *   const toolbar = useToolbarKeyboard();
 *   <div role="toolbar" aria-label="...">
 *     <button {...toolbar.itemProps(0)}>...</button>
 *     <button {...toolbar.itemProps(1)}>...</button>
 *   </div>
 *
 * The first item gets tabIndex=0 by default; arrow keys move focus through
 * the registered items. Home/End jump to ends.
 */
export function useToolbarKeyboard(orientation: "horizontal" | "vertical" = "horizontal") {
  const itemsRef = useRef<(HTMLElement | null)[]>([]);
  const focusedRef = useRef(0);

  const focusItem = useCallback((index: number) => {
    const items = itemsRef.current.filter(Boolean) as HTMLElement[];
    if (!items.length) return;
    const clamped = ((index % items.length) + items.length) % items.length;
    focusedRef.current = clamped;
    items.forEach((el, i) => el.setAttribute("tabindex", i === clamped ? "0" : "-1"));
    items[clamped].focus();
  }, []);

  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLElement>) => {
      const items = itemsRef.current.filter(Boolean) as HTMLElement[];
      if (!items.length) return;
      const current = items.indexOf(document.activeElement as HTMLElement);
      const idx = current >= 0 ? current : focusedRef.current;
      const nextKey = orientation === "horizontal" ? "ArrowRight" : "ArrowDown";
      const prevKey = orientation === "horizontal" ? "ArrowLeft" : "ArrowUp";
      if (e.key === nextKey) {
        e.preventDefault();
        focusItem(idx + 1);
      } else if (e.key === prevKey) {
        e.preventDefault();
        focusItem(idx - 1);
      } else if (e.key === "Home") {
        e.preventDefault();
        focusItem(0);
      } else if (e.key === "End") {
        e.preventDefault();
        focusItem(items.length - 1);
      }
    },
    [focusItem, orientation],
  );

  const itemProps = useCallback(
    (index: number) => ({
      ref: (el: HTMLElement | null) => {
        itemsRef.current[index] = el;
      },
      tabIndex: index === focusedRef.current ? 0 : -1,
      onKeyDown,
      onFocus: () => {
        focusedRef.current = index;
      },
    }),
    [onKeyDown],
  );

  return { itemProps, focusItem };
}

/**
 * WAI-ARIA radiogroup keyboard pattern (single-select toggle group).
 *
 * Arrow keys MOVE FOCUS *and* select the new option (per the radio pattern).
 * Space/Enter activate the focused option (native button behaviour).
 *
 * Usage:
 *   <div role="radiogroup" aria-label="Order">
 *     <button role="radio" aria-checked={value === "a"} {...radio.itemProps(0)}
 *             onClick={() => setValue("a")} onKeyDown={radio.onKeyDown(0, "a")}>A</button>
 *     ...
 *   </div>
 */
export function useRadioGroupKeyboard<T extends string>(
  values: readonly T[],
  current: T,
  setValue: (v: T) => void,
) {
  const itemsRef = useRef<(HTMLElement | null)[]>([]);

  const move = useCallback(
    (nextIndex: number) => {
      const clamped = ((nextIndex % values.length) + values.length) % values.length;
      setValue(values[clamped]);
      requestAnimationFrame(() => {
        itemsRef.current[clamped]?.focus();
      });
    },
    [setValue, values],
  );

  const itemProps = useCallback(
    (index: number) => {
      const value = values[index];
      const isChecked = value === current;
      return {
        ref: (el: HTMLElement | null) => {
          itemsRef.current[index] = el;
        },
        role: "radio" as const,
        "aria-checked": isChecked,
        tabIndex: isChecked ? 0 : -1,
        onKeyDown: (e: KeyboardEvent<HTMLElement>) => {
          if (e.key === "ArrowRight" || e.key === "ArrowDown") {
            e.preventDefault();
            move(index + 1);
          } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
            e.preventDefault();
            move(index - 1);
          } else if (e.key === "Home") {
            e.preventDefault();
            move(0);
          } else if (e.key === "End") {
            e.preventDefault();
            move(values.length - 1);
          } else if (e.key === " " || e.key === "Spacebar") {
            e.preventDefault();
            setValue(value);
          }
        },
      };
    },
    [current, move, setValue, values],
  );

  return { itemProps };
}
